/**
 * ScannerEntradaModal - Modal for scanning barcodes to create stock entries
 * Supports: EAN/GTIN, GS1 DataMatrix, NF-e XML import, Batch scanning mode
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from './ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { ScrollArea } from './ui/scroll-area';
import { 
  Barcode, 
  FileText, 
  Upload, 
  Check, 
  AlertTriangle, 
  Plus,
  Search,
  Loader2,
  QrCode,
  Zap,
  Volume2,
  VolumeX,
  RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';

const ScannerEntradaModal = ({ open, onClose, onEntryCreated }) => {
  const { api } = useAuth();
  const [activeTab, setActiveTab] = useState('produto');
  const [loading, setLoading] = useState(false);
  
  // Barcode scanning state
  const [codigoBarras, setCodigoBarras] = useState('');
  const [scanResult, setScanResult] = useState(null);
  const [scannedItems, setScannedItems] = useState([]);
  
  // Batch scanning mode
  const [batchMode, setBatchMode] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [scanHistory, setScanHistory] = useState([]);
  const [scanStats, setScanStats] = useState({ total: 0, found: 0, notFound: 0 });
  const barcodeInputRef = useRef(null);
  
  // XML import state
  const [xmlFile, setXmlFile] = useState(null);
  const [xmlResult, setXmlResult] = useState(null);
  const fileInputRef = useRef(null);
  
  // NF-e Key consultation state
  const [nfeKey, setNfeKey] = useState('');
  const [nfeResult, setNfeResult] = useState(null);
  
  // New item form
  const [showNewItemForm, setShowNewItemForm] = useState(false);
  const [newItemData, setNewItemData] = useState({
    nome: '',
    tipo: 'material',
    unidade: 'un',
    codigo_barras: ''
  });

  // Sound effects for batch mode
  const playSound = useCallback((type) => {
    if (!soundEnabled) return;
    const audio = new Audio();
    if (type === 'success') {
      // Simple beep using Web Audio API
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = 880; // A5 note
      osc.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.1);
    } else if (type === 'error') {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      osc.type = 'square';
      osc.frequency.value = 220; // A3 note - lower for error
      osc.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.2);
    }
  }, [soundEnabled]);

  // Auto-focus input in batch mode
  useEffect(() => {
    if (batchMode && barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }
  }, [batchMode, scannedItems]);

  // Process barcode in batch mode (auto-submit on Enter or after delay)
  const processBatchScan = useCallback(async (code) => {
    if (!code.trim()) return;
    
    setLoading(true);
    const startTime = Date.now();
    
    try {
      // Determine code type
      let tipoCode = 'ean';
      if (code.length === 44 && /^\d+$/.test(code)) {
        tipoCode = 'nfe_key';
      } else if (code.includes('01') && (code.includes('17') || code.includes('10'))) {
        tipoCode = 'datamatrix';
      }

      const response = await api.post('/estoque/scanner/lookup', {
        codigo: code,
        tipo_codigo: tipoCode
      });

      const scanTime = Date.now() - startTime;
      const historyEntry = {
        codigo: code,
        timestamp: new Date().toISOString(),
        scanTime,
        ...response.data
      };
      
      setScanHistory(prev => [historyEntry, ...prev.slice(0, 49)]); // Keep last 50

      if (response.data.found) {
        // Item found - add to scanned items list
        const newItem = {
          item_id: response.data.item_id,
          item_nome: response.data.item_nome,
          gtin: response.data.gtin,
          lote: response.data.lote || '',
          validade: response.data.validade || '',
          quantidade: response.data.quantidade_sugerida || 1,
          valor_unitario: 0
        };
        
        // Check if already in list - increment quantity
        const existingIndex = scannedItems.findIndex(i => 
          i.item_id === newItem.item_id && i.lote === newItem.lote
        );
        
        if (existingIndex >= 0) {
          setScannedItems(prev => prev.map((i, idx) => 
            idx === existingIndex
              ? {...i, quantidade: i.quantidade + newItem.quantidade}
              : i
          ));
          playSound('success');
          toast.success(`+${newItem.quantidade} ${newItem.item_nome}`, { duration: 1500 });
        } else {
          setScannedItems(prev => [...prev, newItem]);
          playSound('success');
          toast.success(`Adicionado: ${newItem.item_nome}`, { duration: 1500 });
        }
        
        setScanStats(prev => ({ ...prev, total: prev.total + 1, found: prev.found + 1 }));
      } else {
        // Item not found
        playSound('error');
        toast.warning(`Não encontrado: ${code.substring(0, 20)}...`, { duration: 2000 });
        setScanStats(prev => ({ ...prev, total: prev.total + 1, notFound: prev.notFound + 1 }));
        
        // In batch mode, show brief warning but continue
        if (!batchMode) {
          setScanResult(response.data);
          if (response.data.gtin) {
            setNewItemData(prev => ({
              ...prev,
              codigo_barras: response.data.gtin
            }));
            setShowNewItemForm(true);
          }
        }
      }
    } catch (err) {
      console.error('Scan error:', err);
      playSound('error');
      toast.error('Erro ao processar código');
    } finally {
      setLoading(false);
      setCodigoBarras('');
      // Re-focus input for next scan
      if (batchMode && barcodeInputRef.current) {
        setTimeout(() => barcodeInputRef.current?.focus(), 100);
      }
    }
  }, [api, batchMode, scannedItems, playSound]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && codigoBarras.trim()) {
      e.preventDefault();
      if (batchMode) {
        processBatchScan(codigoBarras);
      } else {
        handleScanBarcode();
      }
    }
  };

  const resetBatchStats = () => {
    setScanStats({ total: 0, found: 0, notFound: 0 });
    setScanHistory([]);
  };

  const handleScanBarcode = async () => {
    if (!codigoBarras.trim()) {
      toast.error('Digite ou escaneie um código de barras');
      return;
    }

    setLoading(true);
    try {
      // Determine code type
      const codigo = codigoBarras.trim();
      let tipoCode = 'ean';
      
      if (codigo.length === 44 && /^\d+$/.test(codigo)) {
        tipoCode = 'nfe_key';
      } else if (codigo.includes('01') && (codigo.includes('17') || codigo.includes('10'))) {
        tipoCode = 'datamatrix';
      }

      const response = await api.post('/estoque/scanner/lookup', {
        codigo: codigo,
        tipo_codigo: tipoCode
      });

      setScanResult(response.data);
      
      if (response.data.found) {
        // Item found - add to scanned items list
        const newItem = {
          item_id: response.data.item_id,
          item_nome: response.data.item_nome,
          gtin: response.data.gtin,
          lote: response.data.lote || '',
          validade: response.data.validade || '',
          quantidade: response.data.quantidade_sugerida || 1,
          valor_unitario: 0
        };
        
        // Check if already in list
        const existing = scannedItems.find(i => i.item_id === newItem.item_id && i.lote === newItem.lote);
        if (existing) {
          setScannedItems(prev => prev.map(i => 
            i.item_id === existing.item_id && i.lote === existing.lote
              ? {...i, quantidade: i.quantidade + newItem.quantidade}
              : i
          ));
          toast.info('Quantidade atualizada para item já escaneado');
        } else {
          setScannedItems(prev => [...prev, newItem]);
          toast.success(`Item adicionado: ${newItem.item_nome}`);
        }
        setCodigoBarras('');
      } else if (response.data.gtin) {
        // Item not found but parsed successfully
        setNewItemData(prev => ({
          ...prev,
          codigo_barras: response.data.gtin
        }));
        setShowNewItemForm(true);
        toast.warning('Item não encontrado. Preencha os dados para criar.');
      } else {
        toast.info(response.data.message);
      }
    } catch (err) {
      console.error('Scan error:', err);
      toast.error('Erro ao processar código');
    } finally {
      setLoading(false);
    }
  };

  const handleXMLUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.xml')) {
      toast.error('Selecione um arquivo XML válido');
      return;
    }

    setLoading(true);
    try {
      // Read file as base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result.split(',')[1];
        
        try {
          const response = await api.post('/estoque/importar-xml', {
            xml_content: base64
          });

          setXmlResult(response.data);
          
          if (response.data.success && response.data.itens?.length > 0) {
            toast.success(`${response.data.itens.length} itens encontrados na NF-e`);
          } else {
            toast.warning(response.data.alertas?.[0] || 'Nenhum item encontrado');
          }
        } catch (err) {
          toast.error('Erro ao processar XML');
        } finally {
          setLoading(false);
        }
      };
      reader.readAsDataURL(file);
      setXmlFile(file);
    } catch (err) {
      setLoading(false);
      toast.error('Erro ao ler arquivo');
    }
  };

  // NF-e Key Consultation
  const handleConsultNFe = async () => {
    const cleanKey = nfeKey.replace(/[^0-9]/g, '');
    
    if (cleanKey.length !== 44) {
      toast.error(`Chave inválida: deve ter 44 dígitos (${cleanKey.length} encontrados)`);
      return;
    }

    setLoading(true);
    try {
      const response = await api.post(`/nfe/importar-produtos?chave_acesso=${cleanKey}`);
      
      setNfeResult(response.data);
      
      if (response.data.success && response.data.produtos?.length > 0) {
        const matched = response.data.resumo.itens_encontrados;
        const total = response.data.resumo.total_itens;
        toast.success(`${total} produtos encontrados (${matched} já cadastrados)`);
      } else {
        toast.warning(response.data.alertas?.[0] || 'Nenhum produto encontrado');
      }
    } catch (err) {
      console.error('NF-e consultation error:', err);
      toast.error('Erro ao consultar NF-e');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmNFeImport = () => {
    if (!nfeResult?.produtos?.length) return;

    // Convert NF-e products to scanned items format
    const items = nfeResult.produtos.map(produto => ({
      item_id: produto.item_id,
      item_nome: produto.item_nome || produto.descricao_nfe,
      descricao_original: produto.descricao_nfe,
      gtin: produto.gtin,
      lote: produto.lote || '',
      validade: produto.validade || '',
      quantidade: produto.quantidade,
      valor_unitario: produto.valor_unitario,
      criar_novo: !produto.item_encontrado,
      novo_item: !produto.item_encontrado ? {
        nome: produto.descricao_nfe,
        tipo: 'material',
        unidade: produto.unidade || 'un',
        codigo_barras: produto.gtin
      } : null
    }));

    setScannedItems(items);
    setActiveTab('revisar');
    toast.success('Produtos carregados para revisão');
  };

  const handleConfirmXMLImport = () => {
    if (!xmlResult?.itens?.length) return;

    // Convert XML items to scanned items format
    const items = xmlResult.itens.map(item => ({
      item_id: item.item_id,
      item_nome: item.item_nome_match || item.descricao,
      descricao_original: item.descricao,
      gtin: item.gtin,
      lote: '',
      validade: '',
      quantidade: item.quantidade,
      valor_unitario: item.valor_unitario,
      criar_novo: !item.item_id,
      novo_item: !item.item_id ? {
        nome: item.descricao,
        tipo: 'material',
        unidade: item.unidade || 'un',
        codigo_barras: item.gtin
      } : null
    }));

    setScannedItems(items);
    setActiveTab('revisar');
    toast.success('Itens carregados para revisão');
  };

  const handleCreateNewItem = async () => {
    if (!newItemData.nome.trim()) {
      toast.error('Nome do item é obrigatório');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/estoque/itens', newItemData);
      
      // Add to scanned items
      const newItem = {
        item_id: response.data.id,
        item_nome: response.data.nome,
        gtin: newItemData.codigo_barras,
        lote: scanResult?.lote || '',
        validade: scanResult?.validade || '',
        quantidade: scanResult?.quantidade_sugerida || 1,
        valor_unitario: 0
      };
      
      setScannedItems(prev => [...prev, newItem]);
      setShowNewItemForm(false);
      setNewItemData({ nome: '', tipo: 'material', unidade: 'un', codigo_barras: '' });
      setScanResult(null);
      setCodigoBarras('');
      toast.success('Item criado e adicionado à entrada');
    } catch (err) {
      toast.error('Erro ao criar item');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateScannedItem = (index, field, value) => {
    setScannedItems(prev => prev.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    ));
  };

  const handleRemoveScannedItem = (index) => {
    setScannedItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleConfirmEntry = async () => {
    if (scannedItems.length === 0) {
      toast.error('Adicione pelo menos um item');
      return;
    }

    // Validate items
    const invalidItems = scannedItems.filter(i => !i.quantidade || i.quantidade <= 0);
    if (invalidItems.length > 0) {
      toast.error('Todos os itens devem ter quantidade maior que zero');
      return;
    }

    // Check for items needing creation
    const itemsNeedingCreation = scannedItems.filter(i => !i.item_id && i.criar_novo);
    
    setLoading(true);
    try {
      // Create entrada with all items
      const entradaData = {
        fornecedor: xmlResult?.fornecedor || 'Scanner',
        documento_ref: xmlResult?.numero_nf ? `NF ${xmlResult.numero_nf}` : null,
        data_entrada: new Date().toISOString().split('T')[0],
        itens: scannedItems.map(item => ({
          item_id: item.item_id,
          quantidade: item.quantidade,
          valor_unitario: item.valor_unitario || 0,
          valor_total: (item.quantidade || 0) * (item.valor_unitario || 0),
          lote: item.lote || null,
          validade: item.validade || null,
          criar_novo: item.criar_novo || false,
          novo_item: item.novo_item || null
        }))
      };

      await api.post('/estoque/entradas', entradaData);
      
      toast.success(`Entrada registrada com ${scannedItems.length} item(s)`);
      onEntryCreated?.();
      handleClose();
    } catch (err) {
      console.error('Entry error:', err);
      toast.error('Erro ao registrar entrada');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setScannedItems([]);
    setScanResult(null);
    setXmlResult(null);
    setXmlFile(null);
    setNfeKey('');
    setNfeResult(null);
    setCodigoBarras('');
    setShowNewItemForm(false);
    setActiveTab('produto');
    setBatchMode(false);
    setScanStats({ total: 0, found: 0, notFound: 0 });
    setScanHistory([]);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Barcode className="h-5 w-5" />
            Lançar entrada por código (scanner)
            {batchMode && (
              <Badge variant="secondary" className="ml-2 bg-amber-100 text-amber-800">
                <Zap className="h-3 w-3 mr-1" />
                Modo Lote
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {batchMode 
              ? 'Escaneie múltiplos produtos em sequência. Pressione Enter após cada código.'
              : 'Escaneie códigos de barras ou importe XML de nota fiscal'
            }
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="produto" className="flex items-center gap-2">
              <QrCode className="h-4 w-4" />
              <span className="hidden sm:inline">Produto</span>
              {batchMode && <Zap className="h-3 w-3 text-amber-500" />}
            </TabsTrigger>
            <TabsTrigger value="nfe-chave" className="flex items-center gap-2">
              <Search className="h-4 w-4" />
              <span className="hidden sm:inline">Chave NF-e</span>
            </TabsTrigger>
            <TabsTrigger value="nfe" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              <span className="hidden sm:inline">XML</span>
            </TabsTrigger>
            <TabsTrigger value="revisar" className="flex items-center gap-2">
              <Check className="h-4 w-4" />
              <span className="hidden sm:inline">Revisar</span>
              {scannedItems.length > 0 && (
                <Badge variant="secondary" className="ml-1">{scannedItems.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Product Barcode Tab */}
          <TabsContent value="produto" className="flex-1 space-y-4 overflow-auto">
            {/* Batch Mode Toggle */}
            <div className="flex items-center justify-between bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 p-3 rounded-lg border border-amber-200 dark:border-amber-800">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Switch
                    id="batch-mode"
                    checked={batchMode}
                    onCheckedChange={setBatchMode}
                  />
                  <Label htmlFor="batch-mode" className="flex items-center gap-2 cursor-pointer">
                    <Zap className="h-4 w-4 text-amber-600" />
                    <span className="font-medium">Modo Lote</span>
                  </Label>
                </div>
                {batchMode && (
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSoundEnabled(!soundEnabled)}
                      className="h-8 w-8 p-0"
                      title={soundEnabled ? "Desativar som" : "Ativar som"}
                    >
                      {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={resetBatchStats}
                      className="h-8 px-2 text-xs"
                      title="Resetar estatísticas"
                    >
                      <RotateCcw className="h-3 w-3 mr-1" />
                      Reset
                    </Button>
                  </div>
                )}
              </div>
              {batchMode && (
                <div className="flex gap-3 text-xs">
                  <span className="px-2 py-1 bg-white dark:bg-gray-800 rounded">
                    Total: <strong>{scanStats.total}</strong>
                  </span>
                  <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 rounded text-green-700 dark:text-green-300">
                    ✓ {scanStats.found}
                  </span>
                  <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 rounded text-red-700 dark:text-red-300">
                    ✗ {scanStats.notFound}
                  </span>
                </div>
              )}
            </div>

            <div className={`p-4 rounded-lg ${batchMode ? 'bg-amber-50 dark:bg-amber-900/10 border-2 border-amber-300' : 'bg-muted/30'}`}>
              <Label className="text-sm font-medium">
                {batchMode ? '⚡ Escaneamento Rápido em Lote' : 'Código de barras / DataMatrix'}
              </Label>
              <p className="text-xs text-muted-foreground mb-3">
                {batchMode 
                  ? 'Escaneie ou digite e pressione Enter. O foco permanece no campo para escaneamento contínuo.'
                  : 'Digite ou cole o código EAN/GTIN ou GS1 DataMatrix'
                }
              </p>
              <div className="flex gap-2">
                <Input
                  ref={barcodeInputRef}
                  value={codigoBarras}
                  onChange={(e) => setCodigoBarras(e.target.value)}
                  placeholder={batchMode ? "Aguardando próximo código..." : "Ex: 7891234567890 ou código DataMatrix"}
                  onKeyDown={handleKeyDown}
                  data-testid="input-codigo-barras"
                  autoFocus
                  className={batchMode ? 'text-lg font-mono border-amber-400 focus:border-amber-500' : ''}
                />
                <Button onClick={batchMode ? () => processBatchScan(codigoBarras) : handleScanBarcode} disabled={loading}>
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            {/* Batch Mode - Recent Scans History */}
            {batchMode && scanHistory.length > 0 && (
              <div className="border rounded-lg p-3">
                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <RotateCcw className="h-4 w-4" />
                  Últimos escaneamentos
                </h4>
                <ScrollArea className="h-32">
                  <div className="space-y-1">
                    {scanHistory.slice(0, 10).map((scan, idx) => (
                      <div 
                        key={idx} 
                        className={`flex justify-between items-center text-xs p-2 rounded ${
                          scan.found 
                            ? 'bg-green-50 dark:bg-green-900/20' 
                            : 'bg-red-50 dark:bg-red-900/20'
                        }`}
                      >
                        <span className="font-mono truncate max-w-[200px]">{scan.codigo}</span>
                        <span className="flex items-center gap-2">
                          {scan.found ? (
                            <>
                              <Check className="h-3 w-3 text-green-600" />
                              <span className="text-green-700">{scan.item_nome?.substring(0, 20)}...</span>
                            </>
                          ) : (
                            <>
                              <AlertTriangle className="h-3 w-3 text-red-500" />
                              <span className="text-red-600">Não encontrado</span>
                            </>
                          )}
                          <span className="text-muted-foreground">{scan.scanTime}ms</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}

            {/* Scan Result (non-batch mode) */}
            {!batchMode && scanResult && (
              <div className={`p-4 rounded-lg border ${scanResult.found ? 'bg-green-50 dark:bg-green-900/20 border-green-200' : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200'}`}>
                <div className="flex items-start gap-2">
                  {scanResult.found ? (
                    <Check className="h-5 w-5 text-green-600 mt-0.5" />
                  ) : (
                    <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                  )}
                  <div>
                    <p className="font-medium">{scanResult.found ? scanResult.item_nome : 'Item não encontrado'}</p>
                    <p className="text-sm text-muted-foreground">{scanResult.message}</p>
                    {scanResult.lote && <p className="text-xs">Lote: {scanResult.lote}</p>}
                    {scanResult.validade && <p className="text-xs">Validade: {scanResult.validade}</p>}
                  </div>
                </div>
              </div>
            )}

            {/* New Item Form */}
            {showNewItemForm && !batchMode && (
              <div className="p-4 rounded-lg border bg-background space-y-4">
                <h4 className="font-medium flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Criar novo item no estoque
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nome do item *</Label>
                    <Input
                      value={newItemData.nome}
                      onChange={(e) => setNewItemData(prev => ({...prev, nome: e.target.value}))}
                      placeholder="Nome do produto"
                    />
                  </div>
                  <div>
                    <Label>Tipo</Label>
                    <Select 
                      value={newItemData.tipo} 
                      onValueChange={(v) => setNewItemData(prev => ({...prev, tipo: v}))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="material">Material</SelectItem>
                        <SelectItem value="medicamento">Medicamento</SelectItem>
                        <SelectItem value="equipamento">Equipamento</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Unidade</Label>
                    <Input
                      value={newItemData.unidade}
                      onChange={(e) => setNewItemData(prev => ({...prev, unidade: e.target.value}))}
                      placeholder="un, cx, pc..."
                    />
                  </div>
                  <div>
                    <Label>Código de barras</Label>
                    <Input
                      value={newItemData.codigo_barras}
                      onChange={(e) => setNewItemData(prev => ({...prev, codigo_barras: e.target.value}))}
                      disabled
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleCreateNewItem} disabled={loading}>
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Criar e adicionar
                  </Button>
                  <Button variant="outline" onClick={() => setShowNewItemForm(false)}>
                    Cancelar
                  </Button>
                </div>
              </div>
            )}

            {/* Scanned Items Preview */}
            {scannedItems.length > 0 && (
              <div className="border rounded-lg p-4">
                <h4 className="font-medium mb-2">Itens escaneados ({scannedItems.length})</h4>
                <div className="space-y-2">
                  {scannedItems.slice(0, 5).map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center text-sm bg-muted/30 p-2 rounded">
                      <span>{item.item_nome}</span>
                      <span className="text-muted-foreground">Qtd: {item.quantidade}</span>
                    </div>
                  ))}
                  {scannedItems.length > 5 && (
                    <p className="text-xs text-muted-foreground">+{scannedItems.length - 5} itens...</p>
                  )}
                </div>
                <Button 
                  className="mt-3 w-full" 
                  variant="outline"
                  onClick={() => setActiveTab('revisar')}
                >
                  Revisar todos os itens
                </Button>
              </div>
            )}
          </TabsContent>

          {/* NF-e Key Consultation Tab */}
          <TabsContent value="nfe-chave" className="flex-1 space-y-4 overflow-auto">
            <div className="space-y-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2 flex items-center gap-2">
                  <Search className="h-4 w-4" />
                  Consultar NF-e por Chave de Acesso
                </h4>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  Digite ou cole a chave de acesso de 44 dígitos da nota fiscal para importar os produtos automaticamente.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="nfe-key">Chave de Acesso (44 dígitos)</Label>
                <div className="flex gap-2">
                  <Input
                    id="nfe-key"
                    value={nfeKey}
                    onChange={(e) => setNfeKey(e.target.value)}
                    placeholder="00000000000000000000000000000000000000000000"
                    className="font-mono text-sm"
                    maxLength={50}
                  />
                  <Button 
                    onClick={handleConsultNFe}
                    disabled={loading || nfeKey.replace(/[^0-9]/g, '').length !== 44}
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Search className="h-4 w-4" />
                    )}
                    <span className="ml-2">Consultar</span>
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Dígitos: {nfeKey.replace(/[^0-9]/g, '').length}/44
                </p>
              </div>

              {/* NF-e Result */}
              {nfeResult && (
                <div className="space-y-4 mt-4">
                  {/* NF-e Info */}
                  <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-semibold text-green-900 dark:text-green-100">
                          NF-e #{nfeResult.nfe?.numero}
                        </h4>
                        <p className="text-sm text-green-700 dark:text-green-300">
                          {nfeResult.nfe?.fornecedor}
                        </p>
                        <p className="text-xs text-green-600 dark:text-green-400">
                          CNPJ: {nfeResult.nfe?.cnpj_fornecedor} | Data: {nfeResult.nfe?.data_emissao}
                        </p>
                      </div>
                      <Badge className="bg-green-100 text-green-800">
                        R$ {nfeResult.nfe?.valor_total?.toFixed(2)}
                      </Badge>
                    </div>
                  </div>

                  {/* Warnings */}
                  {nfeResult.alertas?.length > 0 && (
                    <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg border border-amber-200">
                      <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
                        <AlertTriangle className="h-4 w-4" />
                        <span className="text-sm font-medium">Aviso:</span>
                      </div>
                      <ul className="text-xs text-amber-700 dark:text-amber-300 mt-1 list-disc list-inside">
                        {nfeResult.alertas.map((alerta, i) => (
                          <li key={i}>{alerta}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Products Summary */}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">
                      {nfeResult.resumo?.total_itens} produtos encontrados
                    </span>
                    <div className="flex gap-2">
                      <Badge variant="outline" className="bg-green-50 text-green-700">
                        {nfeResult.resumo?.itens_encontrados} cadastrados
                      </Badge>
                      <Badge variant="outline" className="bg-orange-50 text-orange-700">
                        {nfeResult.resumo?.itens_novos} novos
                      </Badge>
                    </div>
                  </div>

                  {/* Products Table */}
                  <ScrollArea className="h-[200px] border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[50px]">#</TableHead>
                          <TableHead>Produto</TableHead>
                          <TableHead className="w-[80px] text-right">Qtd</TableHead>
                          <TableHead className="w-[100px] text-right">Valor Un.</TableHead>
                          <TableHead className="w-[80px]">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {nfeResult.produtos?.map((produto, index) => (
                          <TableRow key={index}>
                            <TableCell className="text-muted-foreground">{produto.numero_item}</TableCell>
                            <TableCell>
                              <div className="flex flex-col">
                                <span className="font-medium text-sm line-clamp-1">
                                  {produto.item_nome || produto.descricao_nfe}
                                </span>
                                {produto.gtin && (
                                  <span className="text-xs text-muted-foreground">GTIN: {produto.gtin}</span>
                                )}
                                {produto.lote && (
                                  <span className="text-xs text-blue-600">Lote: {produto.lote} | Val: {produto.validade}</span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">{produto.quantidade}</TableCell>
                            <TableCell className="text-right">R$ {produto.valor_unitario?.toFixed(2)}</TableCell>
                            <TableCell>
                              {produto.item_encontrado ? (
                                <Badge className="bg-green-100 text-green-700 text-xs">
                                  <Check className="h-3 w-3 mr-1" />
                                  OK
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-orange-600 text-xs">
                                  <Plus className="h-3 w-3 mr-1" />
                                  Novo
                                </Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>

                  {/* Import Button */}
                  <Button 
                    onClick={handleConfirmNFeImport}
                    className="w-full"
                    disabled={!nfeResult.produtos?.length}
                  >
                    <Check className="h-4 w-4 mr-2" />
                    Importar {nfeResult.produtos?.length} produtos para entrada
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>

          {/* NF-e XML Tab */}
          <TabsContent value="nfe" className="flex-1 space-y-4 overflow-auto">
            <div className="bg-muted/30 p-6 rounded-lg border-2 border-dashed text-center">
              <input
                type="file"
                accept=".xml"
                onChange={handleXMLUpload}
                ref={fileInputRef}
                className="hidden"
              />
              <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
              <p className="font-medium">Importar XML da NF-e</p>
              <p className="text-sm text-muted-foreground mb-4">
                Arraste o arquivo XML ou clique para selecionar
              </p>
              <Button onClick={() => fileInputRef.current?.click()} disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
                Selecionar arquivo XML
              </Button>
              {xmlFile && (
                <p className="text-sm mt-2 text-muted-foreground">
                  Arquivo: {xmlFile.name}
                </p>
              )}
            </div>

            {/* XML Import Result */}
            {xmlResult && (
              <div className="border rounded-lg p-4 space-y-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-medium">Dados da NF-e</h4>
                    {xmlResult.fornecedor && <p className="text-sm">Fornecedor: {xmlResult.fornecedor}</p>}
                    {xmlResult.numero_nf && <p className="text-sm">Número: {xmlResult.numero_nf}</p>}
                    {xmlResult.data_emissao && <p className="text-sm">Emissão: {xmlResult.data_emissao}</p>}
                    {xmlResult.valor_total > 0 && <p className="text-sm">Valor Total: R$ {xmlResult.valor_total.toFixed(2)}</p>}
                  </div>
                  <Badge variant={xmlResult.success ? 'default' : 'destructive'}>
                    {xmlResult.itens?.length || 0} itens
                  </Badge>
                </div>

                {/* Alerts */}
                {xmlResult.alertas?.length > 0 && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded text-sm space-y-1">
                    {xmlResult.alertas.map((alerta, idx) => (
                      <p key={idx} className="flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                        {alerta}
                      </p>
                    ))}
                  </div>
                )}

                {/* Items preview */}
                {xmlResult.itens?.length > 0 && (
                  <>
                    <div className="max-h-48 overflow-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Descrição</TableHead>
                            <TableHead className="w-20">Qtd</TableHead>
                            <TableHead className="w-24">Valor Unit.</TableHead>
                            <TableHead className="w-24">Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {xmlResult.itens.map((item, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="text-sm">{item.descricao?.substring(0, 40)}...</TableCell>
                              <TableCell>{item.quantidade}</TableCell>
                              <TableCell>R$ {item.valor_unitario?.toFixed(2)}</TableCell>
                              <TableCell>
                                {item.item_id ? (
                                  <Badge variant="outline" className="text-xs bg-green-50">Encontrado</Badge>
                                ) : (
                                  <Badge variant="outline" className="text-xs bg-amber-50">Novo</Badge>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    <Button onClick={handleConfirmXMLImport} className="w-full">
                      Carregar itens para revisão
                    </Button>
                  </>
                )}
              </div>
            )}
          </TabsContent>

          {/* Review Tab */}
          <TabsContent value="revisar" className="flex-1 flex flex-col min-h-0">
            {scannedItems.length === 0 ? (
              <div className="flex-1 flex items-center justify-center text-muted-foreground">
                <p>Nenhum item adicionado. Escaneie produtos ou importe uma NF-e.</p>
              </div>
            ) : (
              <>
                <ScrollArea className="flex-1 pr-4">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead className="w-24">Quantidade</TableHead>
                        <TableHead className="w-28">Valor Unit.</TableHead>
                        <TableHead className="w-28">Lote</TableHead>
                        <TableHead className="w-32">Validade</TableHead>
                        <TableHead className="w-12"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {scannedItems.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <div>
                              <p className="font-medium text-sm">{item.item_nome}</p>
                              {item.gtin && <p className="text-xs text-muted-foreground">{item.gtin}</p>}
                              {!item.item_id && <Badge variant="outline" className="text-xs mt-1">Criar novo</Badge>}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              min="0.01"
                              step="0.01"
                              value={item.quantidade}
                              onChange={(e) => handleUpdateScannedItem(idx, 'quantidade', parseFloat(e.target.value) || 0)}
                              className="w-20"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.valor_unitario}
                              onChange={(e) => handleUpdateScannedItem(idx, 'valor_unitario', parseFloat(e.target.value) || 0)}
                              className="w-24"
                              placeholder="R$ 0,00"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={item.lote}
                              onChange={(e) => handleUpdateScannedItem(idx, 'lote', e.target.value)}
                              className="w-24"
                              placeholder="Lote"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="date"
                              value={item.validade}
                              onChange={(e) => handleUpdateScannedItem(idx, 'validade', e.target.value)}
                              className="w-32"
                            />
                          </TableCell>
                          <TableCell>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleRemoveScannedItem(idx)}
                              className="text-destructive hover:text-destructive"
                            >
                              ×
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>

                <div className="border-t pt-4 mt-4 flex justify-between items-center">
                  <div className="text-sm text-muted-foreground">
                    {scannedItems.length} item(s) | 
                    Total: R$ {scannedItems.reduce((sum, i) => sum + (i.quantidade * i.valor_unitario), 0).toFixed(2)}
                  </div>
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter className="border-t pt-4">
          <Button variant="outline" onClick={handleClose}>
            Cancelar
          </Button>
          <Button 
            onClick={handleConfirmEntry} 
            disabled={loading || scannedItems.length === 0}
            data-testid="btn-confirmar-entrada"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
            Confirmar e lançar entrada
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ScannerEntradaModal;
